import { createContext, useContext, useLayoutEffect, useState, type ReactNode } from 'react';

/**
 * O centro da barra do topo do celular (AppShell) é um encaixe: a página que tem
 * algo melhor que o próprio nome para pôr ali — no chat, o seletor de agente,
 * como o seletor de modelo do app do Gemini — registra o conteúdo, e a barra o
 * mostra enquanto a página estiver montada. Sem registro o centro fica vazio: o
 * nome da página já é o h1 dela, e repeti-lo na barra o dizia duas vezes.
 *
 * Dois contextos, e não um: quem registra só lê o setter, que é estável, então a
 * página não re-renderiza quando o próprio conteúdo muda. Com um contexto só,
 * renderizar registraria, registrar renderizaria de novo, e assim por diante.
 */
const SetCenterContext = createContext<((node: ReactNode) => void) | null>(null);
const CenterContext = createContext<ReactNode>(null);

export function ShellBarProvider({ children }: { children: ReactNode }): JSX.Element {
  const [center, setCenter] = useState<ReactNode>(null);
  return (
    <SetCenterContext.Provider value={setCenter}>
      <CenterContext.Provider value={center}>{children}</CenterContext.Provider>
    </SetCenterContext.Provider>
  );
}

/** O conteúdo registrado, onde a barra o pinta. Só ele re-renderiza quando o conteúdo muda. */
export function ShellBarCenter(): JSX.Element {
  return <>{useContext(CenterContext)}</>;
}

/**
 * Põe `node` no centro da barra enquanto quem chama estiver montado, e o tira ao
 * desmontar; `null` também limpa (a página passou para a largura do desktop, onde
 * a barra nem existe). Um dono por vez: só uma página mora no <Outlet>. Fora do
 * ShellBarProvider (um teste da página sozinha) não faz nada.
 */
export function useShellBarCenter(node: ReactNode): void {
  const setCenter = useContext(SetCenterContext);
  // De layout, para o conteúdo entrar na barra no mesmo quadro da página. A
  // limpeza e o novo registro de uma atualização caem no mesmo lote do React:
  // a barra nunca pinta o centro vazio entre os dois.
  useLayoutEffect(() => {
    if (!setCenter) return;
    setCenter(node);
    return () => setCenter(null);
  }, [setCenter, node]);
}
